import { supabase } from '@/lib/supabase';
import { portfolioImageBlob } from '@/lib/portfolioImage';

// Pacotes e preços (em centavos). Precisam bater com a função ad_price_cents no banco.
export const AD_PACKAGES = [
  { days: 1, label: '1 dia', priceCents: 3000 },
  { days: 3, label: '3 dias', priceCents: 8000 },
  { days: 7, label: '7 dias', priceCents: 15000 },
];

export const PIX = { key: 'contato@operacaoinvictus.com', name: 'Anderson Ferreira' };

export const formatBRL = (cents) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

async function rpc(fn, params) {
  const { data, error } = await supabase.rpc(fn, params);
  if (error) throw new Error(error.message);
  return data;
}

async function currentUserId() {
  const { data } = await supabase.auth.getUser();
  if (!data?.user) throw new Error('Faça login para continuar.');
  return data.user.id;
}

export const adService = {
  /** Recorta o banner em 1600x400 e envia para o armazenamento. Retorna o endereço público. */
  async uploadBanner(file) {
    const userId = await currentUserId();
    const blob = await portfolioImageBlob(file, 1600, 400);
    const path = `${userId}/${crypto.randomUUID()}.jpg`;
    const { error } = await supabase.storage
      .from('ad-banners')
      .upload(path, blob, { contentType: 'image/jpeg', cacheControl: '3600' });
    if (error) throw new Error(error.message);
    return supabase.storage.from('ad-banners').getPublicUrl(path).data.publicUrl;
  },

  /** Envia o comprovante do Pix para a área privada. Retorna o caminho interno. */
  async uploadProof(file) {
    const userId = await currentUserId();
    const ext = file.type === 'application/pdf' ? 'pdf' : file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
    const path = `${userId}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from('ad-proofs').upload(path, file, { contentType: file.type });
    if (error) throw new Error(error.message);
    return path;
  },

  /** Cria o pedido (aguardando pagamento). Retorna o id da campanha. */
  async request(days, imageUrl, targetUrl) {
    return rpc('request_ad_campaign', { p_days: days, p_image_url: imageUrl, p_target_url: targetUrl });
  },

  async submitProof(campaignId, proofPath) {
    await rpc('submit_ad_proof', { p_id: campaignId, p_proof_path: proofPath });
  },

  /** Campanhas da sua empresa. */
  async mine(companyId) {
    const { data, error } = await supabase
      .from('ad_campaigns')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return data || [];
  },

  /** Números de uma campanha: visualizações, cliques e CTR. */
  async metrics(campaignId) {
    const rows = await rpc('campaign_metrics', { p_campaign: campaignId });
    return rows?.[0] || { views: 0, clicks: 0, ctr: 0 };
  },

  /** Todas as campanhas, para o administrador, com o nome da empresa. */
  async adminList() {
    const { data, error } = await supabase
      .from('ad_campaigns')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    const rows = data || [];
    const ids = [...new Set(rows.map((r) => r.company_id))];
    let names = {};
    if (ids.length) {
      const { data: comps } = await supabase.from('companies').select('id, name').in('id', ids);
      names = Object.fromEntries((comps || []).map((c) => [c.id, c.name]));
    }
    return rows.map((r) => ({ ...r, companyName: names[r.company_id] || 'Empresa' }));
  },

  /** Link temporário (5 minutos) para o administrador ver o comprovante. */
  async proofUrl(path) {
    const { data, error } = await supabase.storage.from('ad-proofs').createSignedUrl(path, 300);
    if (error) throw new Error(error.message);
    return data.signedUrl;
  },

  async approve(campaignId) {
    return rpc('approve_ad_campaign', { p_id: campaignId });
  },

  async reject(campaignId, note) {
    await rpc('reject_ad_campaign', { p_id: campaignId, p_note: note || null });
  },

  async pause(campaignId) {
    await rpc('pause_ad_campaign', { p_id: campaignId });
  },

  async resume(campaignId) {
    await rpc('resume_ad_campaign', { p_id: campaignId });
  },

  async remove(campaignId) {
    await rpc('delete_ad_campaign', { p_id: campaignId });
  },

  /** Banners que aparecem agora na capa (até 10), para qualquer visitante. */
  async activeBanners() {
    return (await rpc('get_active_ads')) || [];
  },

  /** Registra uma visualização ('view') ou um clique ('click'). */
  async track(campaignId, type) {
    try {
      await rpc('track_ad_event', { p_campaign: campaignId, p_type: type });
    } catch (e) {
      // métrica não pode atrapalhar a navegação
    }
  },
};